import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { NotSavedNote } from "@/components/ui/Note";
import { cn } from "@/lib/utils";
import { useCommittedDraft } from "@/hooks/use-committed-draft";
import { Check } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import type {
  Definitions,
  ElementState,
  TitleMatch,
} from "@/lib/element-registry";
import { elementState, figuresSharing } from "@/lib/element-registry";
import { blank } from "@/lib/figure-status";
import type { TableRow } from "@/lib/figure-registry";
import type { NamedFigure } from "./TitleOffers";
import {
  TitleOffers,
  comboboxProps,
  figureLabel,
  keyOnOffers,
  offersId,
  useTitleOfferList,
} from "./TitleOffers";
import type { Placement } from "./AnnotatedDrawing";
import {
  NestMenu,
  NestingDrag,
  TakeOutZone,
  useNestable,
  type JoinOffer,
} from "./ComponentNesting";

/**
 * The rows of a figure's component table: a plain element, a sub-category's own row, or a
 * member drawn under it, each with its numeral, its title and its confirmation.
 */

/**
 * What the stored model accepts for an element title.
 *
 * Checked before the write rather than left to the endpoint, because a refusal
 * is a refusal of the whole set.
 */
const MAX_TITLE = 200;

/**
 * The numeral column is a fixed 3.25rem so four digits fit (1500 under tens)
 * and every title starts at the same place. The title takes the rest of the
 * row, and a row's confirm and options sit on a line under it, so a component's
 * whole name shows in a narrow panel.
 */
export const ROW_GRID =
  "grid grid-cols-[1rem_3.25rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1";

/**
 * The numeral as it is drawn in the table: the same pill the drawing puts on the artwork.
 */
const NUMERAL_PILL =
  "inline-grid h-6 min-w-[30px] place-items-center rounded-full border-[1.5px] font-mono text-[11.5px] font-semibold";

/**
 * What the pill says about the element before anyone has picked its pin.
 *
 * An element with no title is drawn open, in the dashed amber the drawing gives the same
 * state, so the row that still wants something reads as unfinished from the number alone.
 */
const PILL_STATES: Record<ElementState, string> = {
  "needs-title":
    "border-2 border-dashed border-warning bg-card text-warning-strong",
  titled: "border-border bg-card text-muted-foreground",
  confirmed: "border-border bg-card text-muted-foreground",
};

/**
 * How each state marks its row.
 *
 * Only the unfinished one is marked. A row that is titled, or titled and confirmed, says so
 * through its numeral and its status, and ruling a colour down the side of every row as well
 * leaves the table striped in three colours with nothing standing out of it.
 */
const ROW_STYLES: Record<ElementState, string> = {
  "needs-title": "shadow-[inset_0_0_0_1px_var(--color-warning)]",
  titled: "",
  confirmed: "",
};

/** The other drawings that carry this element. */
function SharedWith({
  definitions,
  figures,
  fileId,
  elementId,
}: {
  definitions: Definitions;
  figures: NamedFigure[];
  fileId: string;
  elementId: string;
}) {
  const elsewhere = figuresSharing(definitions, elementId).filter(
    (id) => id !== fileId,
  );
  if (elsewhere.length === 0) return null;
  return (
    <span className="col-span-3 col-start-2 -mt-1 flex flex-wrap gap-1">
      {elsewhere.map((id) => (
        <span
          key={id}
          className="rounded-full border border-border bg-muted px-1.5 py-px text-[10px] text-muted-foreground"
        >
          {figureLabel(figures, id)}
        </span>
      ))}
    </span>
  );
}

/**
 * Where the row has got to: an act while there is one left, and a statement once there is not.
 *
 * Confirmed stops being a button. It is the end of the row's road and there is nothing to
 * press, so a greyed control that cannot be used only invites the attempt.
 */
function Status({
  elementId,
  state,
  untitled,
  onConfirm,
}: {
  elementId: string;
  state: ElementState;
  untitled: boolean;
  onConfirm: (elementId: string) => void;
}) {
  if (state === "confirmed") {
    return (
      <span className="flex items-center gap-1 whitespace-nowrap rounded-[5px] bg-success-surface px-[7px] py-[3px] text-[9px] font-bold uppercase tracking-[0.03em] text-success-strong">
        <Check className="size-3" aria-hidden />
        Confirmed
      </span>
    );
  }
  return (
    <Button
      variant="confirm"
      size="dense"
      type="button"
      onClick={() => {
        onConfirm(elementId);
      }}
      // An untitled row has nothing to confirm, the rule Confirm all already follows.
      disabled={untitled}
    >
      <Check aria-hidden />
      Confirm
    </Button>
  );
}

/**
 * The derived digits this element shows.
 *
 * A typed override is the office-action seam, after the filed set is locked.
 * Define does not offer that field: the convention chooses the number.
 */
function NumberCell({
  numeral,
  state,
}: {
  numeral: string | undefined;
  state: ElementState;
}) {
  return (
    <span className={`${NUMERAL_PILL} ${PILL_STATES[state]}`}>
      {numeral === undefined ? "" : numeral}
    </span>
  );
}

/**
 * How the title reads.
 *
 * An underline rather than a box: the title is written straight into the row, and a box
 * around every one of them turns the table into a form. It reads the same whether or not
 * anything is written in it, because the row's own ring and its numeral already say that
 * one is wanted, and a third amber thing on the same row says it three times.
 */
const TITLE_FIELD =
  "w-full border-b border-dashed border-border bg-transparent px-px py-1 text-[13.5px] font-semibold placeholder:font-normal placeholder:text-muted-foreground/60 focus:border-ring focus:outline-none";

/** The title's words wrap and the field grows with them, so a long name is read whole. */
const TITLE_WRAPS = "mx-0 overflow-hidden leading-snug";

/** A field as tall as its words, where the browser cannot size one to its content itself. */
function useGrownToWords(words: string) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const field = ref.current;
    if (field === null || CSS.supports("field-sizing", "content")) return;
    field.style.height = "auto";
    field.style.height = `${String(field.scrollHeight)}px`;
  }, [words]);
  return ref;
}

/**
 * The title, with the elements the set already holds offered against what is typed.
 *
 * Free entry is what the field is for and the offers sit beside it rather than inside it:
 * typing filters them, leaving the field still saves whatever was typed, and a lookup with
 * nothing to say renders nothing at all. There is no mode to enter and none to leave.
 *
 * Picking another element hands this one's marks over to it, which is the same merge the
 * number cell makes when digits another element already shows are typed into it. Picking
 * the element being edited is a completion instead, since there is nothing to hand over: a
 * prefix of an element's own stored title still reaches it.
 */
function TitleCell({
  definitions,
  assigned,
  figures,
  elementId,
  field,
  onReuse,
  offering = true,
}: {
  definitions: Definitions;
  assigned: Map<string, string>;
  figures: NamedFigure[];
  elementId: string;
  /** The title as stored, the words typed, and their saves. */
  field: ReturnType<typeof useCommittedDraft> & { stored: string };
  onReuse: (fromElementId: string, toElementId: string) => void;
  /**
   * A group row names a set, and no element's marks can be handed to it, so it offers none,
   * and its field is named for the sub-category it titles.
   */
  offering?: boolean;
}) {
  const { stored, draft, setDraft, commit } = field;
  const offers = useTitleOfferList(definitions, offering ? draft : "");
  const fieldRef = useGrownToWords(draft);

  const pick = (match: TitleMatch) => {
    offers.close();
    if (match.elementId !== elementId) {
      // The draft goes back to what the set holds, so the flush that saves half-typed words
      // has nothing to say about an element whose last mark is leaving.
      setDraft(stored);
      onReuse(elementId, match.elementId);
      return;
    }
    setDraft(match.title);
    commit(match.title);
  };

  return (
    <div className="relative min-w-0">
      <Textarea
        appearance="line"
        ref={fieldRef}
        rows={1}
        aria-label={`${offering ? "Element" : "Sub-category"} title for ${assigned.get(elementId) ?? elementId}`}
        {...comboboxProps(elementId, offers.matches, offers.highlighted)}
        value={draft}
        // The endpoint refuses a longer title, and refuses the whole set with it, so a pasted
        // essay would take an unrelated edit queued behind it down as well.
        maxLength={MAX_TITLE}
        placeholder={offering ? "Name this element" : "Name this sub-category"}
        onChange={(event) => {
          offers.onTyped();
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          keyOnOffers(event, {
            matches: offers.matches,
            highlighted: offers.highlighted,
            setHighlighted: offers.setHighlighted,
            pick,
            // The same path the field's own blur takes, so the guard against writing a
            // title nobody changed still stands and Enter twice costs one write.
            submit: () => {
              offers.close();
              commit(draft);
            },
          });
        }}
        onBlur={() => {
          // The list closes with the field. Left open it would hang over the card with the
          // keys that dismiss it going to whatever was focused next. A pick survives this
          // because the press on an option prevents its own default and keeps the focus.
          offers.close();
          commit(draft);
        }}
        className={cn(TITLE_FIELD, TITLE_WRAPS)}
      />
      <TitleOffers
        matches={offers.matches}
        highlighted={offers.highlighted}
        assigned={assigned}
        figures={figures}
        listId={offersId(elementId)}
        onPick={pick}
      />
      <NotSavedNote failed={field.failed} onRetry={field.retry} />
    </div>
  );
}

/** A component's row in the table, the place the Terminology panel lands on. */
export function registryRowId(elementId: string): string {
  return `registry-row-${elementId}`;
}

/** What every row needs to draw itself, beyond which element it is. */
interface SharedProps {
  definitions: Definitions;
  figures: NamedFigure[];
  fileId: string;
  assigned: Map<string, string>;
  selected: Placement | undefined;
  onRetitle: (elementId: string, title: string) => Promise<void>;
  servedTitles: ReadonlyMap<string, string>;
  onConfirm: (elementId: string) => void;
  onReuse: (fromElementId: string, toElementId: string) => void;
}

/** What a row needs to draw itself. */
type RowProps = SharedProps & { elementId: string };

/** The numeral, the title and the status every row draws, a group row included. */
function RowCells({
  definitions,
  figures,
  elementId,
  assigned,
  onRetitle,
  servedTitles,
  onConfirm,
  onReuse,
  offering,
  trailing,
}: RowProps & { offering: boolean; trailing?: ReactNode }) {
  // The acknowledged title, not the view's: the view folds the pending rename in, and a
  // refusal would take it back to the old name and the typing with it.
  const stored = servedTitles.get(elementId) ?? "";
  const field = useCommittedDraft(stored, (title) =>
    onRetitle(elementId, title),
  );
  const state = elementState(definitions, elementId);
  return (
    <>
      <NumberCell numeral={assigned.get(elementId)} state={state} />
      <TitleCell
        definitions={definitions}
        assigned={assigned}
        figures={figures}
        elementId={elementId}
        field={{ ...field, stored }}
        onReuse={onReuse}
        offering={offering}
      />
      <span className="col-start-3 flex items-center gap-1.5">
        <Status
          elementId={elementId}
          state={state}
          untitled={blank(field.draft)}
          onConfirm={onConfirm}
        />
        {trailing}
      </span>
    </>
  );
}

/** The row classes every row shares: its grid, its rule, its state and whether it is picked. */
function rowClass(props: RowProps, extra = ""): string {
  const state = elementState(props.definitions, props.elementId);
  const picked =
    props.selected?.elementId === props.elementId ? "bg-muted" : "";
  return `${ROW_GRID} border-b border-border px-[14px] py-2 ${ROW_STYLES[state]} ${picked} ${extra}`;
}

/**
 * A sub-category's own row: its numeral, its title and its confirmation, and nothing to drag.
 *
 * It names the set ("the crystals 20") and carries no pins, so it is neither carried nor a
 * place to drop: a component joins the group by a drop on one of its members.
 */
function GroupRow(props: RowProps) {
  const numeral = props.assigned.get(props.elementId);
  return (
    <div
      id={registryRowId(props.elementId)}
      data-testid={`define-element-row-${numeral ?? props.elementId}`}
      data-place="group"
      data-state={elementState(props.definitions, props.elementId)}
      className={rowClass(props)}
    >
      <span aria-hidden />
      <RowCells {...props} offering={false} />
    </div>
  );
}

/** A component row: an element of its own, or a member drawn under its group. */
function ElementRow({
  place,
  joinable,
  onNest,
  onTakeOut,
  ...props
}: RowProps & {
  place: "element" | "member";
  joinable: JoinOffer[];
  onNest: (dragged: string, target: string) => void;
  onTakeOut: (elementId: string) => void;
}) {
  const { definitions, figures, fileId, elementId, assigned } = props;
  const numeral = assigned.get(elementId) ?? "";
  const title =
    definitions.registry.find((r) => r.element_id === elementId)?.title ?? "";
  const nestable = useNestable({
    elementId,
    label: `Make ${[numeral, title.trim()].filter(Boolean).join(" ")} a sub-category of another component`,
  });

  return (
    <div
      ref={nestable.ref}
      id={registryRowId(elementId)}
      data-testid={`define-element-row-${numeral || elementId}`}
      data-place={place}
      data-state={elementState(definitions, elementId)}
      className={rowClass(
        props,
        `${nestable.chrome} ${place === "member" ? "pl-8" : ""}`,
      )}
    >
      {nestable.handle}
      <RowCells
        {...props}
        offering
        trailing={
          <NestMenu
            numeral={numeral}
            member={place === "member"}
            others={joinable}
            onJoin={(target) => {
              onNest(elementId, target);
            }}
            onTakeOut={() => {
              onTakeOut(elementId);
            }}
          />
        }
      />
      <SharedWith
        definitions={definitions}
        figures={figures}
        fileId={fileId}
        elementId={elementId}
      />
    </div>
  );
}

/** A component as the menu and the drag name it: its numeral and its title. */
function labelOf(
  props: Pick<SharedProps, "definitions" | "assigned">,
  elementId: string,
): string {
  const title =
    props.definitions.registry.find((r) => r.element_id === elementId)?.title ??
    "";
  const numeral = props.assigned.get(elementId) ?? "";
  return [numeral, title.trim()].filter(Boolean).join(" ") || elementId;
}

/** The components a row may join: every other one on this figure outside its own group. */
function joinOffers(
  rows: TableRow[],
  props: Pick<SharedProps, "definitions" | "assigned">,
  elementId: string,
): JoinOffer[] {
  const groupOf = (id: string) =>
    props.definitions.registry.find((r) => r.element_id === id)?.group_id ??
    null;
  const own = groupOf(elementId);
  return rows
    .filter(
      (row) =>
        row.place !== "group" &&
        row.elementId !== elementId &&
        (own === null || groupOf(row.elementId) !== own),
    )
    .map((row) => ({
      elementId: row.elementId,
      label: labelOf(props, row.elementId),
    }));
}

/** Every row of the table, inside the drag that nests one component under another. */
export function RegistryRows({
  rows,
  onNest,
  onTakeOut,
  ...shared
}: SharedProps & {
  rows: TableRow[];
  onNest: (dragged: string, target: string) => void;
  onTakeOut: (elementId: string) => void;
}) {
  return (
    <NestingDrag
      name={(elementId) => labelOf(shared, elementId)}
      onNest={onNest}
      onTakeOut={onTakeOut}
    >
      {rows.map((row) =>
        row.place === "group" ? (
          <GroupRow key={row.elementId} {...shared} elementId={row.elementId} />
        ) : (
          <ElementRow
            // Keyed by the element so a row that changes displayed digits keeps the field
            // rather than carrying another element's half-typed title onto it.
            key={row.elementId}
            {...shared}
            elementId={row.elementId}
            place={row.place}
            joinable={joinOffers(rows, shared, row.elementId)}
            onNest={onNest}
            onTakeOut={onTakeOut}
          />
        ),
      )}
      {rows.some((row) => row.place === "group") && <TakeOutZone />}
    </NestingDrag>
  );
}
